import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bot, RotateCcw, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../contexts/ToastContext';
import {
  AiPromptDefinition,
  CATEGORY_LABELS,
  AiPromptCategory,
} from '../../constants/aiPromptDefaults';

const AdminAiPromptsPage: React.FC = () => {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [prompts, setPrompts] = useState<AiPromptDefinition[]>([]);
  const [selectedId, setSelectedId] = useState<string>('resume_analysis');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const fetchPrompts = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/ai-prompts', { credentials: 'include' });
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setPrompts(data.data);
        setSelectedId((current) =>
          data.data.some((p: AiPromptDefinition) => p.id === current)
            ? current
            : data.data[0]?.id || 'resume_analysis'
        );
      } else {
        error(data.message || 'Failed to load AI prompts');
      }
    } catch {
      error('Failed to load AI prompts');
    } finally {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  const selected = useMemo(
    () => prompts.find((p) => p.id === selectedId) || null,
    [prompts, selectedId]
  );

  const grouped = useMemo(() => {
    const map = new Map<AiPromptCategory, AiPromptDefinition[]>();
    for (const prompt of prompts) {
      const list = map.get(prompt.category) || [];
      list.push(prompt);
      map.set(prompt.category, list);
    }
    return map;
  }, [prompts]);

  const updateSelected = (patch: Partial<AiPromptDefinition>) => {
    setPrompts((prev) =>
      prev.map((p) => (p.id === selectedId ? { ...p, ...patch } : p))
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch('/api/admin/ai-prompts', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompts }),
      });
      const data = await response.json();
      if (data.success) {
        setPrompts(data.data);
        success('AI prompts saved');
      } else {
        error(data.message || data.errors?.join('; ') || 'Save failed');
      }
    } catch {
      error('Failed to save AI prompts');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selected) return;
    if (!window.confirm(`Reset "${selected.label}" to the built-in default?`)) return;

    setResetting(true);
    try {
      const response = await fetch(`/api/admin/ai-prompts/${selected.id}/reset`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      if (data.success) {
        setPrompts(data.data);
        success('Prompt reset to default');
      } else {
        error(data.message || 'Reset failed');
      }
    } catch {
      error('Failed to reset prompt');
    } finally {
      setResetting(false);
    }
  };

  const insertVariable = (varName: string) => {
    if (!selected) return;
    const token = `{{${varName}}}`;
    updateSelected({ promptTemplate: `${selected.promptTemplate}${selected.promptTemplate.endsWith('\n') ? '' : '\n'}${token}` });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <p className="text-gray-600">Loading AI prompts…</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate('/admin/dashboard')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to dashboard
          </Button>
          <h1 className="text-3xl font-bold text-secondary flex items-center gap-2">
            <Bot className="h-8 w-8 text-orange-600" />
            AI Prompt Manager
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            Configure Bedrock (Nova Lite) prompts per use case. Runtime code loads prompts by id — e.g.{' '}
            <code className="text-xs bg-gray-100 px-1 rounded">resume_analysis</code> for resume ATS analysis.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={!selected || resetting || saving}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to default
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving…' : 'Save all prompts'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <Card className="p-4 h-fit space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Use cases</h2>
          {[...grouped.entries()].map(([category, items]) => (
            <div key={category}>
              <p className="text-xs font-medium text-gray-500 mb-2">{CATEGORY_LABELS[category] || category}</p>
              <ul className="space-y-1">
                {items.map((prompt) => (
                  <li key={prompt.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(prompt.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedId === prompt.id
                          ? 'bg-orange-100 text-orange-900 font-medium'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <span className="block">{prompt.label}</span>
                      <span className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                        <span className={`inline-block w-2 h-2 rounded-full ${prompt.enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                        {prompt.status === 'planned' ? 'Planned' : prompt.enabled ? 'Active' : 'Disabled'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </Card>

        {selected ? (
          <Card className="p-6 space-y-6">
            <div>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{selected.label}</h2>
                  <p className="text-sm text-gray-600 mt-1">{selected.description}</p>
                  <p className="text-xs text-gray-400 mt-2 font-mono">id: {selected.id}</p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    checked={selected.enabled}
                    onChange={(e) => updateSelected({ enabled: e.target.checked })}
                    className="rounded border-gray-300 text-orange-600"
                  />
                  <span className="text-sm font-medium text-gray-700">Enabled</span>
                </label>
              </div>
              {selected.status === 'planned' && (
                <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  This use case is registered for future ingestion pipelines. You can edit the prompt now; wire-up in code when Discord/Telegram job bots are ready.
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Model override</label>
                <Input
                  value={selected.modelId}
                  onChange={(e) => updateSelected({ modelId: e.target.value })}
                  placeholder="amazon.nova-lite-v1:0 (env default)"
                  className="font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Temperature</label>
                <Input
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={selected.temperature}
                  onChange={(e) => updateSelected({ temperature: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max tokens</label>
                <Input
                  type="number"
                  min={256}
                  max={8192}
                  step={256}
                  value={selected.maxTokens}
                  onChange={(e) => updateSelected({ maxTokens: parseInt(e.target.value, 10) || 4096 })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Prompt template</label>
              <Textarea
                value={selected.promptTemplate}
                onChange={(e) => updateSelected({ promptTemplate: e.target.value })}
                className="font-mono text-sm min-h-[360px] leading-relaxed"
                spellCheck={false}
              />
              <p className="text-xs text-gray-500 mt-2">
                Use double-brace placeholders. The backend replaces them at runtime before sending to Bedrock.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Available variables</h3>
              <ul className="space-y-2">
                {selected.variables.map((v) => (
                  <li key={v.name} className="flex flex-wrap items-start gap-2 text-sm">
                    <button
                      type="button"
                      onClick={() => insertVariable(v.name)}
                      className="font-mono text-xs bg-white border border-gray-300 px-2 py-0.5 rounded hover:border-orange-400 hover:text-orange-700"
                    >
                      {`{{${v.name}}}`}
                    </button>
                    <span className="text-gray-600">
                      {v.description}
                      {v.required && <span className="text-red-600 ml-1">*</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </Card>
        ) : (
          <Card className="p-8 text-center text-gray-500">Select a prompt to edit</Card>
        )}
      </div>
    </div>
  );
};

export default AdminAiPromptsPage;
